import { DateTime } from 'luxon'

// Luxon only — the dashboard also carries moment and moment-timezone (README §10.2).
export const formatDay = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso).toFormat('d LLL yyyy') : '—'

// Accepted track starts are a Monday at midnight UTC; read in local time they would fall on Sunday in the US.
export const formatStartDay = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso, { zone: 'utc' }).toFormat('d LLL yyyy') : '—'

export const formatDayTime = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso).toFormat('d LLL yyyy, HH:mm') : '—'

// A missing patient zone must not silently display the coach's time.
export const formatDayTimeIn = (iso: string | null | undefined, timezone: string | null | undefined): string | null => {
  const date = iso && timezone ? DateTime.fromISO(iso, { zone: timezone }) : null

  return date?.isValid ? date.toFormat('d LLL yyyy, HH:mm') : null
}

export const formatDays = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`

// The dates a coach reads and types on a history entry are month/day/year, as in Standard, whatever the locale.
export const formatUsDate = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso).toFormat('M/d/yyyy') : '—'

export const formatUsDateTime = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso).toFormat("M/d/yyyy 'at' h:mm a") : '—'

export const wholeDaysSince = (iso: string | null | undefined): number | null => {
  if (!iso) {
    return null
  }

  const { days } = DateTime.now().diff(DateTime.fromISO(iso), 'days')

  return Math.max(0, Math.floor(days))
}

export const relativeDay = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso).toRelativeCalendar() ?? formatDay(iso) : '—'

// `PRE_ENROLLED` → `Pre Enrolled`, the way Standard's participant status reads.
export const humanizeState = (state: string): string =>
  state
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ')

export const humanizeRole = (role: string): string => {
  const spaced = role.replace(/[_-]+/g, ' ').trim()

  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : role
}

export const joinName = (firstname: string | null | undefined, lastname: string | null | undefined): string =>
  [firstname, lastname].filter(Boolean).join(' ')

export const ageFrom = (birthDate: string | null | undefined): number | null => {
  if (!birthDate) {
    return null
  }

  const date = DateTime.fromISO(birthDate)
  if (!date.isValid || date > DateTime.now()) {
    return null
  }

  return Math.floor(DateTime.now().diff(date, 'years').years)
}

// Null without the patient's zone rather than falling back to the coach's, which would mislead.
export const localTimeIn = (timezone: string | null | undefined): string | null => {
  if (!timezone) {
    return null
  }

  const now = DateTime.now().setZone(timezone)

  return now.isValid ? now.toFormat('h:mm a') : null
}

export const zoneAbbreviationIn = (timezone: string | null | undefined): string | null => {
  const now = timezone ? DateTime.now().setZone(timezone) : null

  return now?.isValid ? now.toFormat('ZZZZ') : null
}

// Accepts epoch millis as well as ISO: Firebase stores numbers, `server` returns strings.
export const relativeFrom = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const date = typeof value === 'number' ? DateTime.fromMillis(value) : DateTime.fromISO(value)

  return date.isValid ? date.toRelative() : null
}

// Codegen types `Date`/`DateTime` scalars as JS `Date`, but they arrive as ISO strings and nothing parses them.
export const toIso = (value: string | Date | null | undefined): string | null => {
  if (!value) {
    return null
  }

  return value instanceof Date ? value.toISOString() : value
}

// Standard's feed time, moment's `h:mm a`.
export const formatTime = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso).setLocale('en-US').toFormat('h:mm a').toLowerCase() : '—'

// Standard's call and lab-event time, `VideoCallItem/formatDate`.
export const formatEventTime = (iso: string | null | undefined): string =>
  iso ? DateTime.fromISO(iso).toFormat('EEE MMM d, h:mm a') : '—'

const ordinal = (day: number): string => {
  const lastTwo = day % 100
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${day}th`
  }

  return `${day}${['th', 'st', 'nd', 'rd'][day % 10] ?? 'th'}`
}

// Standard's day divider (`formatRelativeTime`), counted in calendar days so a label can't change within a day.
export const chatDayLabel = (iso: string | null | undefined): string => {
  if (!iso) {
    return '—'
  }
  const date = DateTime.fromISO(iso).setLocale('en-US')
  const now = DateTime.now()
  const daysAgo = Math.floor(now.startOf('day').diff(date.startOf('day'), 'days').days)
  const dateLabel = date.toFormat('ccc, LLL d')

  if (daysAgo === 0) {
    return `${dateLabel} · Today`
  }
  if (daysAgo === 1) {
    return `${dateLabel} · Yesterday`
  }
  if (daysAgo >= 2 && daysAgo <= 6) {
    return `${dateLabel} · ${daysAgo} days ago`
  }

  return date.hasSame(now, 'year')
    ? `${date.toFormat('ccc, LLL')} ${ordinal(date.day)}`
    : `${date.toFormat('LLL')} ${ordinal(date.day)}, ${date.year}`
}

export const todayIso = (): string => DateTime.now().toISODate() as string
